/**
 * Committing to a jump.
 *
 * Picking a destination is not the same as going there, and this is the gap
 * between them: a held key, a run-up, and a tunnel you cannot steer out of.
 *
 * ── Why it is a hold and not a press ────────────────────────────────────────
 *
 * The confirm key is the dock key, and it is held for five seconds. Both of
 * those are on purpose. Sharing the key means "commit to the thing under the
 * reticle" is one idea with one binding rather than two rules a player has to
 * keep apart; and a jump takes the ship out of the system, drops it beside a
 * star it has never seen, and cannot be called off — so it should not be
 * possible to do by brushing a key. Five seconds is long enough that letting go
 * is a decision too.
 *
 * ── Why the phases are here and not in the renderer ─────────────────────────
 *
 * All of it is arithmetic over elapsed time: how far into the hold, whether the
 * run-up has reached warp speed, whether the tunnel has run its minimum. The
 * flight loop has the clock and the velocity; it should not also own the rules
 * about what they mean, because then the only way to check them is to fly.
 */
/** How long the confirm key is held before the target is locked, in seconds. */
export const JUMP_CONFIRM_HOLD = 5;
/**
 * The shortest a jump ever appears to take, in seconds.
 *
 * The tunnel also has to cover unloading one system and generating another, and
 * that is not a fixed cost — a sparse system comes up in a moment and a crowded
 * one does not. A floor means the crossing has the same weight either way
 * rather than being an eyeblink when the galaxy happens to be kind.
 */
export const JUMP_TUNNEL_SECONDS = 15;
/**
 * How much longer a crossing between clusters takes.
 *
 * Three times, because it is three orders of something further. A hop inside a
 * cluster covers a few thousand galactic units; leaving one covers ten thousand
 * and up, and it costs a consumable to attempt. If both took the same fifteen
 * seconds the core would be the only thing separating them, and the distance
 * would be a number in a label rather than something you sat through.
 */
export const JUMP_CLUSTER_MULTIPLIER = 3;
/** How long this particular crossing takes, in seconds. */
export function tunnelSecondsFor(crossingCluster) {
    return JUMP_TUNNEL_SECONDS * (crossingCluster ? JUMP_CLUSTER_MULTIPLIER : 1);
}
/**
 * What the drive gains once a target is locked.
 *
 * The run-up is a scripted stretch with no decisions in it, so the only thing
 * its length can do is bore. Tripling it cuts that to a third without touching
 * how the ship handles when the player is actually flying it — none of this
 * applies while anyone has the controls.
 */
export const JUMP_RUN_ACCELERATION = 3;
/**
 * The ceiling while a jump is locked, in world units per second.
 *
 * Above `WARP_SPEED`, and that is the point: a jump is not a warp run that
 * happens to end somewhere else, it is the drive doing something it cannot
 * sustain. The gauge running past its own red line is the plainest way to say
 * so, and it removes the oddity of a "jump" that topped out at exactly the
 * speed you can reach any afternoon.
 */
export const JUMP_MAX_SPEED = 1000;
/**
 * Where the lights go out.
 *
 * In fiction, a jump is flown on mechanical instruments because nothing
 * electrical survives it; the console shuts down on the way in and comes back
 * on the far side.
 *
 * It also solves a real problem. The crossing rebuilds the ship's whole
 * configuration — warp off, gear up, thruster set reset — and every switch
 * visibly flipping at once looked like a fault. Now the console is dark before
 * any of that happens and lit again after, so the flip is something that
 * happened while you could not see it rather than something done to you.
 */
export const JUMP_BLACKOUT_SPEED = 700;
/**
 * How long the console keeps its feet after the lock, whatever the speed.
 *
 * ── Why a speed threshold alone was not enough ──────────────────────────────
 *
 * The blackout is written as "once the run passes `JUMP_BLACKOUT_SPEED`", which
 * reads as a ship accelerating into the dark. It is not what happens: a jump
 * needs the warp drive lit, warp tops out at 800, and the threshold is 700 — so
 * a pilot who confirms while actually *travelling* is already past it, and the
 * console dies on the first frame of the run.
 *
 * That leaves no gap between "jump locked" and total darkness, so the module has
 * nowhere to say what it is doing and the shutdown reads as a fault rather than
 * as a sequence. Holding power for a moment regardless of speed gives the run a
 * beginning; the speed rule then takes over exactly as before.
 */
export const JUMP_CALIBRATION = 2;
/**
 * Where the drive stops being polite about it.
 *
 * A second doubling on top of `JUMP_RUN_ACCELERATION`, once the console is
 * already dark. The last three hundred units are the fastest part of the run,
 * which is what makes the blackout feel like a threshold rather than a wait.
 */
export const JUMP_SURGE_SPEED = 800;
/**
 * Speed the run-up has to reach before the tunnel opens.
 *
 * Raised to the new ceiling so the shutdown has room to play: locking at 800 —
 * perfectly possible, since that is ordinary warp — used to open the tunnel on
 * the same frame, with no run-up at all.
 */
export const JUMP_TUNNEL_SPEED = JUMP_MAX_SPEED;
/**
 * How long the view takes to close in once the tunnel opens, in seconds.
 *
 * The streaks, the gas and the shell around the ship all wind up on this one
 * number, because they are one event: the ship goes. It used to be spread
 * across the run-up, with the shell darkening as the drive built speed — which
 * made entering a jump a slow dimming rather than a jump.
 *
 * It is also the moment the destination is swapped in. By then nothing of the
 * old system is visible, so the work of tearing one down and generating another
 * happens behind a black screen with thirteen seconds of crossing still to run,
 * instead of against the arrival.
 */
export const JUMP_SWAP_AT = 2.2;
export const NO_JUMP = { phase: 'idle', systemId: null, elapsed: 0 };
/** How far through the confirm hold, 0..1. Zero unless actually confirming. */
export function confirmProgress(sequence) {
    if (sequence.phase !== 'confirming')
        return 0;
    return Math.min(1, sequence.elapsed / JUMP_CONFIRM_HOLD);
}
/** Whether the pilot still has the ship. False from the lock onward. */
export function pilotHasControl(sequence) {
    return sequence.phase === 'idle' || sequence.phase === 'confirming';
}
/**
 * Whether the ship's electrics are running.
 *
 * False from the moment a locked run passes `JUMP_BLACKOUT_SPEED` until the
 * crossing is over. The caller keeps it false a little longer — through the run
 * in from the system edge — because coming back up while still moving would put
 * the console on before the ship had arrived.
 */
export function systemsPowered(sequence, speed) {
    if (sequence.phase === 'tunnel')
        return false;
    if (sequence.phase !== 'running')
        return true;
    // The run gets a beginning, whatever speed it was entered at. See above.
    if (sequence.elapsed < JUMP_CALIBRATION)
        return true;
    return Math.abs(speed) < JUMP_BLACKOUT_SPEED;
}
/** How much the drive is over-delivering right now, as a multiplier. */
export function runAcceleration(sequence, speed) {
    if (sequence.phase !== 'running')
        return 1;
    return Math.abs(speed) >= JUMP_SURGE_SPEED ? JUMP_RUN_ACCELERATION * 2 : JUMP_RUN_ACCELERATION;
}
/** Whether the tunnel is up, and the world should be hidden behind it. */
export function inTunnel(sequence) {
    return sequence.phase === 'tunnel';
}
/**
 * How dark the shell around the ship should be, 0..1.
 *
 * Winds up with the streaks and holds until the crossing ends — the fade back
 * out belongs to the arrival, which is a different clock and a different piece
 * of the sequence.
 */
export function crossingVeil(sequence) {
    if (sequence.phase !== 'tunnel')
        return 0;
    return Math.min(1, sequence.elapsed / JUMP_SWAP_AT);
}
/**
 * Whether the view is now dark enough to change the world behind it.
 *
 * Deliberately not "the tunnel has finished": doing the swap at the end put the
 * cost of generating a system directly against the arrival animation, which is
 * the one moment of the crossing anybody is looking at.
 */
export function readyToSwap(sequence) {
    return sequence.phase === 'tunnel' && sequence.elapsed >= JUMP_SWAP_AT;
}
/**
 * Advance the sequence by one frame.
 *
 * Returns the next state. `'idle'` with a systemId is never produced — an
 * abandoned confirm forgets its target, so releasing the key and pressing it
 * again starts the five seconds over rather than resuming them.
 */
export function stepJump(sequence, conditions) {
    const { step, aimed, confirming, armed, speed, loaded } = conditions;
    switch (sequence.phase) {
        case 'idle': {
            if (!armed || !confirming || !aimed)
                return NO_JUMP;
            return { phase: 'confirming', systemId: aimed, elapsed: step };
        }
        case 'confirming': {
            // Anything that made this target valid going away drops the hold. The
            // reticle wandering off it counts: you are aiming at a system, and half
            // an aim is not a commitment.
            if (!armed || !confirming || aimed !== sequence.systemId)
                return NO_JUMP;
            const elapsed = sequence.elapsed + step;
            if (elapsed < JUMP_CONFIRM_HOLD)
                return { ...sequence, elapsed };
            return { phase: 'running', systemId: sequence.systemId, elapsed: 0 };
        }
        case 'running': {
            // No way out from here, deliberately. The key can be released, the module
            // switched off, the reticle pointed anywhere — the ship is committed and
            // the pilot is a passenger. That is what the five seconds bought.
            const elapsed = sequence.elapsed + step;
            if (speed < JUMP_TUNNEL_SPEED)
                return { ...sequence, elapsed };
            return { phase: 'tunnel', systemId: sequence.systemId, elapsed: 0 };
        }
        case 'tunnel': {
            const elapsed = sequence.elapsed + step;
            if (elapsed < tunnelSecondsFor(conditions.crossingCluster ?? false) || !loaded) {
                return { ...sequence, elapsed };
            }
            return NO_JUMP;
        }
    }
}
/**
 * Whether this step is the one that lands the ship.
 *
 * Asked of the transition rather than the state, because arriving is an event —
 * the caller has to move the ship exactly once, and a flag on the resulting
 * state would be true for however many frames it took them to notice.
 */
export function hasArrived(before, after) {
    return before.phase === 'tunnel' && after.phase === 'idle';
}
/** Whether this step is the one that commits the ship, for the same reason. */
export function hasLocked(before, after) {
    return before.phase === 'confirming' && after.phase === 'running';
}
/** Whether the tunnel has just opened, which is when loading should start. */
export function hasEnteredTunnel(before, after) {
    return before.phase === 'running' && after.phase === 'tunnel';
}
//# sourceMappingURL=jumpSequence.js.map